/* assets/defer-below-fold-slideshows.js
 *
 * Purpose:
 * - Below-the-fold carousels: do NOT initialize until they scroll near the viewport
 * - Below-the-fold videos: do NOT load sources or play until near viewport
 * - If a carousel/video was already initialized/autoplaying, we pause/stop it until in view
 *
 * Works with:
 * - Flickity carousels (if present)
 * - HTML5 <video> tags (MP4)
 *
 * Markup requirements:
 * - Carousel root: class="js-defer-carousel" data-defer="true"
 * - If you want us to init Flickity: data-init-flickity="true" + data-flickity-options='{"wrapAround":true,...}'
 * - Video tags: <video data-defer-video> with <source data-src="..."> (NOT src)
 */

(function () {
  function qsAll(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function parseJSON(str, fallback) {
    try {
      return JSON.parse(str);
    } catch (e) {
      return fallback;
    }
  }

  function hasIntersectionObserver() {
    return "IntersectionObserver" in window && "IntersectionObserverEntry" in window;
  }

  function isReducedMotion() {
    try {
      return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (e) {
      return false;
    }
  }

  function setVideoAutoplayOff(video) {
    // Hard stop for videos that sneak autoplay in through attributes or JS
    try {
      video.autoplay = false;
      video.removeAttribute("autoplay");
      video.pause();
    } catch (e) {}
  }

  function unloadVideoSources(video) {
    // Optional: keep video unloaded until in view
    // If already has src, we don't strip it (can cause flashes). We only control data-src workflow.
    try {
      // Ensure it won't aggressively preload
      if (!video.getAttribute("preload")) video.setAttribute("preload", "none");
    } catch (e) {}
  }

  function loadVideoSources(video) {
    // If already loaded, do nothing
    if (video.dataset.sourcesLoaded === "1") return;

    var sources = qsAll("source[data-src]", video);
    if (sources.length === 0) {
      // Fallback: if someone left src directly on <video>, mark loaded and proceed
      video.dataset.sourcesLoaded = "1";
      return;
    }

    sources.forEach(function (s) {
      var ds = s.getAttribute("data-src");
      if (ds && !s.getAttribute("src")) s.setAttribute("src", ds);
    });

    // Some themes also put data-src on <video>
    var videoDataSrc = video.getAttribute("data-src");
    if (videoDataSrc && !video.getAttribute("src")) video.setAttribute("src", videoDataSrc);

    video.dataset.sourcesLoaded = "1";

    try {
      // Change preload now that we’re in view
      video.setAttribute("preload", "metadata");
      video.load();
    } catch (e) {}
  }

  function playVideoSafe(video) {
    // Don’t force play for reduced motion users
    if (isReducedMotion()) return;

    // Only play if intended
    if (video.dataset.playInView === "false") return;

    // Must be muted for autoplay to work on mobile
    if (!video.muted) video.muted = true;

    var p;
    try {
      p = video.play();
    } catch (e) {
      return;
    }
    if (p && typeof p.catch === "function") {
      p.catch(function () {
        // Autoplay blocked; ignore
      });
    }
  }

  function pauseVideoSafe(video) {
    try {
      video.pause();
    } catch (e) {}
  }

  function pauseAllVideosIn(root) {
    qsAll("video", root).forEach(function (v) {
      setVideoAutoplayOff(v);
      pauseVideoSafe(v);
    });
  }

  function prepareDeferredVideos(root) {
    qsAll("video[data-defer-video]", root).forEach(function (v) {
      setVideoAutoplayOff(v);
      unloadVideoSources(v);
      // Default behavior: play when in view unless explicitly disabled
      if (!v.dataset.playInView) v.dataset.playInView = "true";
    });
  }

  function getFlickityInstance(el) {
    if (!window.Flickity || typeof window.Flickity.data !== "function") return null;
    try {
      return window.Flickity.data(el);
    } catch (e) {
      return null;
    }
  }

  function stopFlickityAutoplay(el) {
    var flkty = getFlickityInstance(el);
    if (!flkty) return;
    try {
      // Make sure it can’t restart itself unless we explicitly start it
      flkty.options.autoPlay = false;
      flkty.stopPlayer();
    } catch (e) {}
  }

  function initFlickityIfNeeded(el) {
    // Only init if explicitly told to
    if (el.dataset.initFlickity !== "true") return;

    // Prevent double init
    if (el.dataset.flickityInit === "1") return;

    function doInit() {
      if (!window.Flickity) {
        setTimeout(doInit, 50);
        return;
      }

      // If theme already initialized it, just mark and stop autoplay until we decide otherwise
      var existing = getFlickityInstance(el);
      if (existing) {
        el.dataset.flickityInit = "1";
        stopFlickityAutoplay(el);
        return;
      }

      var opts = parseJSON(el.getAttribute("data-flickity-options") || "{}", {});
      // Always enforce: no autoplay at init for below-fold
      opts.autoPlay = false;

      try {
        // eslint-disable-next-line no-new
        new window.Flickity(el, opts);
        el.dataset.flickityInit = "1";
      } catch (e) {
        // If init fails, don't spam
        el.dataset.flickityInit = "1";
      }
    }

    doInit();
  }

  function onEnter(el) {
    // Init carousel if needed
    initFlickityIfNeeded(el);

    // Load + play deferred videos inside
    qsAll("video[data-defer-video]", el).forEach(function (v) {
      loadVideoSources(v);
      playVideoSafe(v);
    });

    // If you explicitly want Flickity autoplay when in view, you can set:
    // data-autoplay-in-view="true" data-autoplay-speed="7000"
    var flkty = getFlickityInstance(el);
    if (flkty && el.dataset.autoplayInView === "true" && !isReducedMotion()) {
      var speed = parseInt(el.dataset.autoplaySpeed || "7000", 10);
      try {
        flkty.options.autoPlay = speed;
        flkty.playPlayer();
      } catch (e) {}
    }
  }

  function onExit(el) {
    // Pause videos
    qsAll("video", el).forEach(function (v) {
      pauseVideoSafe(v);
    });

    // Stop Flickity autoplay if any
    stopFlickityAutoplay(el);
  }

  function boot() {
    var carousels = qsAll(".js-defer-carousel[data-defer='true']");
    if (carousels.length === 0) return;

    // Immediately stop/pause anything already running
    carousels.forEach(function (el) {
      stopFlickityAutoplay(el);
      prepareDeferredVideos(el);
      pauseAllVideosIn(el);
    });

    if (!hasIntersectionObserver()) {
      // Fallback: init/load everything immediately (older browsers)
      carousels.forEach(function (el) {
        onEnter(el);
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var el = entry.target;
          if (entry.isIntersecting) onEnter(el);
          else onExit(el);
        });
      },
      {
        // Start “near” viewport so video loads before user sees it
        root: null,
        rootMargin: "300px 0px 300px 0px",
        threshold: 0.01
      }
    );

    carousels.forEach(function (el) {
      io.observe(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
